import Phaser from 'phaser';
import {
    getModuleDefinition,
    getSymbol,
    getSymbolsData,
    ModuleDefinition,
    PlayerModule,
    SymbolDraw,
} from '../data/ModuleData';
import { OrbColor } from '../config/GameConfig';

// Affinity colors matching the design
const AFFINITY_COLORS: Record<string, number> = {
    THERMAL: 0xe74c3c,
    CRYO: 0x3498db,
    RADIATION: 0x2ecc71,
    EMP: 0xf1c40f,
    VOID: 0x9b59b6,
};

// CRT Reactor Display Gem Colors
const GEM_PALETTE: Record<OrbColor, { core: number; glow: number }> = {
    R: { core: 0xFF4A3D, glow: 0xB61F1A },
    B: { core: 0x2FA0FF, glow: 0x0F4C8A },
    G: { core: 0x2BFF9A, glow: 0x0F7A4F },
    Y: { core: 0xFFD24A, glow: 0x8A6A12 },
    P: { core: 0xB06CFF, glow: 0x4F1E86 },
    S: { core: 0xF2F6FF, glow: 0x7A8CA3 },
};

const BEZEL_FILL = 0x141821;
const BEZEL_STROKE = 0x2A3140;
const WINDOW_BORDER = 0x0B0E14;
const GLYPH_COLOR = 0xD7DEE8;

// Affinity to OrbColor mapping
const AFFINITY_TO_ORB: Record<string, OrbColor> = {
    THERMAL: 'R',
    CRYO: 'B',
    RADIATION: 'G',
    EMP: 'Y',
    VOID: 'P',
};

// Rarity colors
const RARITY_COLORS: Record<string, number> = {
    common: 0x666666,
    rare: 0x3498db,
    officer: 0x9b59b6,
};

const RARITY_GLOW: Record<string, number> = {
    common: 0x444444,
    rare: 0x2980b9,
    officer: 0x8e44ad,
};

export interface ModuleCardOptions {
    size: number;
    showLevel?: boolean;
    showAffinities?: boolean;
    isFitted?: boolean;
    isSelected?: boolean;
}

export class ModuleRenderer {
    private scene: Phaser.Scene;

    constructor(scene: Phaser.Scene) {
        this.scene = scene;
    }

    /**
     * Create a module card container with icon, border, level, and affinity gems
     */
    createModuleCard(
        playerModule: PlayerModule,
        options: ModuleCardOptions
    ): Phaser.GameObjects.Container | null {
        const definition = getModuleDefinition(playerModule.definitionId);
        if (!definition) return null;

        const { size, showLevel = true, showAffinities = true, isFitted = false, isSelected = false } = options;
        const container = this.scene.add.container(0, 0);

        // Background
        const bg = this.scene.add.graphics();
        this.drawCardBackground(bg, size, definition.rarity, isFitted, isSelected);
        container.add(bg);

        // Symbol icon
        const symbolGraphics = this.scene.add.graphics();
        this.drawSymbol(symbolGraphics, definition.symbolId, size * 0.6, definition.slotType);
        container.add(symbolGraphics);

        // Level indicator (top-left) - minimum 24px font for accessibility
        if (showLevel) {
            const levelText = playerModule.level >= 10 ? 'MAX' : `${playerModule.level}`;
            const fontSize = Math.max(24, Math.floor(size * 0.22));
            const levelLabel = this.scene.add.text(
                -size / 2 + 8,
                -size / 2 + 6,
                levelText,
                {
                    fontFamily: 'monospace',
                    fontSize: `${fontSize}px`,
                    color: playerModule.level >= 10 ? '#f1c40f' : '#ffffff',
                    fontStyle: 'bold',
                }
            );
            container.add(levelLabel);
        }

        // CMP value (top-right)
        const costFontSize = Math.max(24, Math.floor(size * 0.20));
        const cmpLabel = this.scene.add.text(
            size / 2 - 8,
            -size / 2 + 6,
            `${definition.cmpCost}`,
            {
                fontFamily: 'monospace',
                fontSize: `${costFontSize}px`,
                color: '#2ecc71',  // Green for CMP
                fontStyle: 'bold',
            }
        );
        cmpLabel.setOrigin(1, 0);  // Right-align
        container.add(cmpLabel);

        // RCT value (bottom-left)
        const rctLabel = this.scene.add.text(
            -size / 2 + 8,
            size / 2 - 6,
            `${definition.rctCost}`,
            {
                fontFamily: 'monospace',
                fontSize: `${costFontSize}px`,
                color: '#3498db',  // Blue for RCT
                fontStyle: 'bold',
            }
        );
        rctLabel.setOrigin(0, 1);  // Bottom-left align
        container.add(rctLabel);

        // Affinity gems (bottom-right) - using instance aspects
        const moduleAspects = playerModule.aspects || [];
        if (showAffinities && moduleAspects.length > 0) {
            const gemSize = size * 0.24;
            const startX = size / 2 - gemSize / 2 - 4;
            const startY = size / 2 - gemSize / 2 - 4;

            for (let i = 0; i < Math.min(moduleAspects.length, 2); i++) {
                const aspect = moduleAspects[i];
                const gemX = startX - i * (gemSize / 2 + 4);

                const gemGraphics = this.scene.add.graphics();
                this.drawAffinityGem(gemGraphics, gemX, startY, gemSize, aspect);
                container.add(gemGraphics);
            }
        }

        // Store data for interaction
        container.setData('playerModule', playerModule);
        container.setData('definition', definition);
        container.setSize(size, size);

        return container;
    }

    private drawCardBackground(
        graphics: Phaser.GameObjects.Graphics,
        size: number,
        rarity: string,
        isFitted: boolean,
        isSelected: boolean
    ): void {
        const half = size / 2;
        const borderWidth = 3;
        const cornerRadius = 6;

        // Outer glow for selection
        if (isSelected) {
            graphics.lineStyle(4, 0xffffff, 0.5);
            graphics.strokeRoundedRect(-half - 2, -half - 2, size + 4, size + 4, cornerRadius + 2);
        }

        // Border based on rarity
        const borderColor = isFitted ? 0x2ecc71 : RARITY_COLORS[rarity] || 0x666666;
        graphics.lineStyle(borderWidth, borderColor, 1);
        graphics.strokeRoundedRect(-half, -half, size, size, cornerRadius);

        // Dark background with subtle gradient effect
        graphics.fillStyle(0x1a1a2e, 1);
        graphics.fillRoundedRect(-half + borderWidth, -half + borderWidth, size - borderWidth * 2, size - borderWidth * 2, cornerRadius - 2);

        // Inner panel effect
        graphics.fillStyle(0x16161f, 1);
        graphics.fillRoundedRect(-half + 6, -half + 6, size - 12, size - 12, 4);
    }

    private drawSymbol(
        graphics: Phaser.GameObjects.Graphics,
        symbolId: string,
        size: number,
        slotType: string
    ): void {
        const symbol = getSymbol(symbolId);
        if (!symbol) {
            // Fallback: draw a simple shape
            graphics.lineStyle(2, 0x888888, 1);
            graphics.strokeCircle(0, 0, size * 0.3);
            return;
        }

        // Color based on slot type
        const color = slotType === 'weapon' ? 0xcccccc : 0x88ccff;

        for (const draw of symbol.draws) {
            this.executeDrawCommand(graphics, draw, size, color);
        }
    }

    private executeDrawCommand(
        graphics: Phaser.GameObjects.Graphics,
        draw: SymbolDraw,
        size: number,
        color: number
    ): void {
        const scale = size;

        switch (draw.type) {
            case 'line':
                graphics.lineStyle(draw.width || 2, color, 1);
                graphics.lineBetween(
                    (draw.x1 || 0) * scale,
                    (draw.y1 || 0) * scale,
                    (draw.x2 || 0) * scale,
                    (draw.y2 || 0) * scale
                );
                break;

            case 'circle':
                if (draw.fill) {
                    graphics.fillStyle(color, 1);
                    graphics.fillCircle(
                        (draw.x || 0) * scale,
                        (draw.y || 0) * scale,
                        (draw.radius || 0.1) * scale
                    );
                } else if (draw.stroke) {
                    graphics.lineStyle(2, color, 1);
                    graphics.strokeCircle(
                        (draw.x || 0) * scale,
                        (draw.y || 0) * scale,
                        (draw.radius || 0.1) * scale
                    );
                } else {
                    graphics.fillStyle(color, 1);
                    graphics.fillCircle(
                        (draw.x || 0) * scale,
                        (draw.y || 0) * scale,
                        (draw.radius || 0.1) * scale
                    );
                }
                break;

            case 'rect':
                if (draw.fill === false) {
                    graphics.lineStyle(2, color, 1);
                    graphics.strokeRect(
                        (draw.x || 0) * scale,
                        (draw.y || 0) * scale,
                        (draw.w || 0.1) * scale,
                        (draw.h || 0.1) * scale
                    );
                } else {
                    graphics.fillStyle(color, 1);
                    graphics.fillRect(
                        (draw.x || 0) * scale,
                        (draw.y || 0) * scale,
                        (draw.w || 0.1) * scale,
                        (draw.h || 0.1) * scale
                    );
                }
                break;

            case 'arc':
                graphics.lineStyle(draw.width || 2, color, 1);
                graphics.beginPath();
                graphics.arc(
                    (draw.x || 0) * scale,
                    (draw.y || 0) * scale,
                    (draw.radius || 0.1) * scale,
                    draw.start || 0,
                    draw.end || Math.PI * 2
                );
                graphics.strokePath();
                break;

            case 'polygon':
                if (draw.points && draw.points.length >= 4) {
                    const points: number[] = [];
                    for (let i = 0; i < draw.points.length; i++) {
                        points.push(draw.points[i] * scale);
                    }

                    if (draw.stroke) {
                        graphics.lineStyle(2, color, 1);
                        graphics.beginPath();
                        graphics.moveTo(points[0], points[1]);
                        for (let i = 2; i < points.length; i += 2) {
                            graphics.lineTo(points[i], points[i + 1]);
                        }
                        graphics.closePath();
                        graphics.strokePath();
                    } else {
                        graphics.fillStyle(color, 1);
                        graphics.beginPath();
                        graphics.moveTo(points[0], points[1]);
                        for (let i = 2; i < points.length; i += 2) {
                            graphics.lineTo(points[i], points[i + 1]);
                        }
                        graphics.closePath();
                        graphics.fillPath();
                    }
                }
                break;
        }
    }

    private drawAffinityGem(
        graphics: Phaser.GameObjects.Graphics,
        x: number,
        y: number,
        size: number,
        affinity: string
    ): void {
        const orbColor = AFFINITY_TO_ORB[affinity] || 'P';
        const palette = GEM_PALETTE[orbColor];
        // Match board gem proportions: half = size/2, chamfer = 22% of full size (44% of half)
        const half = size / 2;
        const chamfer = size * 0.22;

        // Helper to get octagon points centered at origin
        const getOctagonPoints = (h: number, c: number): number[] => [
            -h + c, -h, h - c, -h, h, -h + c, h, h - c,
            h - c, h, -h + c, h, -h, h - c, -h, -h + c,
        ];

        // Translate all points to x,y center
        const translatePoints = (pts: number[]): number[] => {
            const result: number[] = [];
            for (let i = 0; i < pts.length; i += 2) {
                result.push(pts[i] + x, pts[i + 1] + y);
            }
            return result;
        };

        const outerPoints = translatePoints(getOctagonPoints(half, chamfer));
        const innerInset = half * 0.12;
        const innerHalf = half - innerInset;
        const innerChamfer = chamfer * 0.85;
        const innerPoints = translatePoints(getOctagonPoints(innerHalf, innerChamfer));

        // Layer 1: Outer bezel
        graphics.fillStyle(BEZEL_FILL, 1);
        graphics.beginPath();
        graphics.moveTo(outerPoints[0], outerPoints[1]);
        for (let i = 2; i < outerPoints.length; i += 2) {
            graphics.lineTo(outerPoints[i], outerPoints[i + 1]);
        }
        graphics.closePath();
        graphics.fillPath();

        // Bezel stroke
        graphics.lineStyle(1, BEZEL_STROKE, 1);
        graphics.strokePath();

        // Inner shadow edge
        graphics.lineStyle(1, 0x000000, 0.4);
        graphics.beginPath();
        graphics.moveTo(innerPoints[0], innerPoints[1]);
        for (let i = 2; i < innerPoints.length; i += 2) {
            graphics.lineTo(innerPoints[i], innerPoints[i + 1]);
        }
        graphics.closePath();
        graphics.strokePath();

        // Layer 2: Window
        const windowInset = half * 0.18;
        const windowHalf = half - windowInset;
        const windowChamfer = chamfer * 0.7;
        const windowPoints = translatePoints(getOctagonPoints(windowHalf, windowChamfer));

        graphics.fillStyle(WINDOW_BORDER, 1);
        graphics.beginPath();
        graphics.moveTo(windowPoints[0], windowPoints[1]);
        for (let i = 2; i < windowPoints.length; i += 2) {
            graphics.lineTo(windowPoints[i], windowPoints[i + 1]);
        }
        graphics.closePath();
        graphics.fillPath();

        // Layer 3: Emissive screen
        const screenInset = half * 0.22;
        const screenHalf = half - screenInset;
        const screenChamfer = chamfer * 0.6;
        const screenPoints = translatePoints(getOctagonPoints(screenHalf, screenChamfer));

        graphics.fillStyle(palette.core, 0.9);
        graphics.beginPath();
        graphics.moveTo(screenPoints[0], screenPoints[1]);
        for (let i = 2; i < screenPoints.length; i += 2) {
            graphics.lineTo(screenPoints[i], screenPoints[i + 1]);
        }
        graphics.closePath();
        graphics.fillPath();
    }

    /**
     * Draw a simplified module icon (for ship hardpoints)
     */
    drawModuleIcon(
        graphics: Phaser.GameObjects.Graphics,
        definition: ModuleDefinition,
        size: number
    ): void {
        this.drawSymbol(graphics, definition.symbolId, size, definition.slotType);
    }

    /**
     * Create an empty slot placeholder
     */
    createEmptySlot(size: number, slotType: 'weapon' | 'support'): Phaser.GameObjects.Container {
        const container = this.scene.add.container(0, 0);

        const graphics = this.scene.add.graphics();
        const half = size / 2;

        // Dashed border effect
        graphics.lineStyle(2, slotType === 'weapon' ? 0x664444 : 0x446688, 0.5);
        graphics.strokeRoundedRect(-half, -half, size, size, 6);

        // Inner dark area
        graphics.fillStyle(0x0a0a0f, 0.8);
        graphics.fillRoundedRect(-half + 3, -half + 3, size - 6, size - 6, 4);

        // Plus sign
        const plusSize = size * 0.2;
        graphics.lineStyle(2, slotType === 'weapon' ? 0x664444 : 0x446688, 0.4);
        graphics.lineBetween(-plusSize, 0, plusSize, 0);
        graphics.lineBetween(0, -plusSize, 0, plusSize);

        container.add(graphics);
        container.setSize(size, size);
        container.setData('slotType', slotType);
        container.setData('isEmpty', true);

        return container;
    }
}
